import React, { useState } from 'react';
import { exportData, ExportFormat, FieldMapping } from '../../utils/importExportService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getImportExportLang } from '../../translations/imp_lang';
import toast from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

interface ExportButtonProps {
  data: Record<string, any>[];
  mappings: FieldMapping[];
  filename: string;
  label?: string;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  mappings,
  filename,
  label,
}) => {
  const { language } = useLanguage();
  const tr = getImportExportLang(language);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleExport = (format: ExportFormat) => {
    if (data.length === 0) {
      toast.error(tr.import.noData);
      setShowDropdown(false);
      return;
    }

    try {
      exportData(data, mappings, filename, format);
      toast.success(tr.import.exportSuccess.replace('{count}', String(data.length)).replace('{format}', format.toUpperCase()));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(tr.import.exportError);
    }
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
      >
        <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
        {label || tr.import.export}
      </button>

      {showDropdown && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDropdown(false)}
          />
          <div className="absolute right-0 mt-2 w-44 rounded-xl shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-20 overflow-hidden">
            <button
              onClick={() => handleExport('csv')}
              className="w-full flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <DocumentTextIcon className="w-4 h-4 mr-2 text-gray-400" />
              {tr.import.exportCSV}
            </button>
            <button
              onClick={() => handleExport('xlsx')}
              className="w-full flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <TableCellsIcon className="w-4 h-4 mr-2 text-gray-400" />
              {tr.import.exportExcel}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportButton;
